/**
 * Shared CSS styles for the application
 * 
 * This module provides common styles to avoid duplication across components.
 * Apply these styles once to the document head when needed.
 */

/**
 * Common button styles used across the application
 */
export const BUTTON_STYLES = `
    /* Primary submit/action button */
    .mm-submit-button, .submit-button {
        background-color: #007BFF;
        color: #fff;
        border: none;
        border-radius: 8px;
        margin: 0.125rem;
        padding: clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.75rem, 2.5vw, 1rem);
        font-size: clamp(0.875rem, 2.5vw, 1rem);
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s, transform 0.1s;
        min-height: 44px;
        touch-action: manipulation;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }

    .mm-submit-button:hover, .submit-button:hover {
        background-color: #0056b3;
    }

    .mm-submit-button:active, .submit-button:active {
        transform: scale(0.98);
    }

    /* Mobile adjustments for submit buttons */
    @media (max-width: 768px) {
        .mm-submit-button, .submit-button {
            width: 100%;
            max-width: none;
        }
    }
`;

/**
 * Common modal/container styles used across the application
 */
export const MODAL_STYLES = `
    /* Modal container overlay */
    .modal-container, .menu-modal-container {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        display: none;
        justify-content: center;
        align-items: center;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.6);
        z-index: 1000;
        padding: 1rem;
        overflow-y: auto;
    }

    /* Modal content box */
    .modal-content {
        background-color: #fff;
        border-radius: 15px;
        padding: 20px;
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
        text-align: center;
        margin: 5px;
    }
`;

/**
 * Common input field styles used across the application
 */
export const INPUT_STYLES = `
    /* Text input fields */
    .mm-text-input, input[type="text"].username-input {
        border: 1px solid #00d9ff;
        border-radius: 8px;
        padding: clamp(0.375rem, 1.5vw, 0.5rem);
        font-size: clamp(0.875rem, 2.5vw, 1rem);
        font-weight: 600;
        width: 100%;
        max-width: 400px;
        min-height: 44px;
        transition: all 0.3s ease;
        background: rgba(10, 15, 30, 0.8);
        color: #00ffff;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 10px rgba(0, 217, 255, 0.2);
    }

    .mm-text-input:focus, input[type="text"].username-input:focus {
        outline: none;
        border-color: #00ffff;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 255, 255, 0.5), 0 0 30px rgba(0, 255, 157, 0.3);
    }

    .mm-text-input::placeholder, input[type="text"].username-input::placeholder {
        color: rgba(0, 217, 255, 0.5);
    }

    /* Legacy username-input specific styles (only for text inputs) */
    input[type="text"].username-input {
        border: 1px solid #00d9ff;
        border-radius: 25px;
        padding: 10px;
        font-size: 16px;
    }
`;

/**
 * Base responsive styles
 */
export const BASE_STYLES = `
    * {
        box-sizing: border-box;
    }
`;

/**
 * All shared styles combined
 */
export const ALL_SHARED_STYLES = `
${BASE_STYLES}
${MODAL_STYLES}
${BUTTON_STYLES}
${INPUT_STYLES}
`;

/**
 * Apply shared styles to the document head
 * Only injects styles once, even if called multiple times
 */
let stylesApplied = false;

export function applySharedStyles(): void {
    if (stylesApplied) return;
    
    const style = document.createElement('style');
    style.id = 'shared-styles';
    style.textContent = ALL_SHARED_STYLES;
    document.head.appendChild(style);
    
    stylesApplied = true;
}

/**
 * Helper to apply component-specific styles that extend shared styles
 * @param css Component-specific CSS to add
 * @param componentId Optional ID for the style tag
 */
export function applyComponentStyles(css: string, componentId?: string): void {
    const style = document.createElement('style');
    if (componentId) {
        style.id = `${componentId}-styles`;
    }
    style.textContent = css;
    document.head.appendChild(style);
}
